import { createElement, Fragment, ReactNode } from 'react';

/** File-scope constants for the lobby internal view helpers. */
const localConstants = {
  // Paired inline-block delimiter: {N}...{N}, where N indexes the style class to wrap the enclosed run in. The \1 backreference forces the closing index to match the opening one, so {0}..{0} and
  // {2}..{2} never cross-pair. These markers are decorative (not interpolation params), so the translator leaves them in place exactly like positional params.
  inlineBlockPattern: /\{(\d+)\}([\s\S]*?)\{\1\}/g,
  // A literal newline in a Localized string, rendered as a hard line break rather than relying on CSS white-space.
  lineBreak: '\n',
  brTag: 'br',
  spanTag: 'span',
  // Phone formatting: a North American local number is exactly 10 digits (3 area + 3 exchange + 4 line); any leading digits are the country code.
  phoneNonDigit: /\D+/g,
  phoneLocalLength: 10,
  phoneAreaEnd: 3,
  phoneExchangeEnd: 6,
  // Nested-section markers in formatted answers: a `{0}...{0}` sub-header run at the start of a blank-line-separated chunk starts a nested collapsible section; chunks before the first section
  // are the parent head, chunks after the last section's chunk are the parent tail.
  nestedSectionMarkerIndex: 0,
  blankLinePattern: /\n\s*\n/,
  paragraphJoin: '\n\n'
} as const;

/**
 * Formats an E.164 phone number for display as `+<country> (AAA) EEE-LLLL` (e.g. `+15551234567` -> `+1 (555) 123-4567`). When no country-code digits remain the leading group is omitted (`(555)
 * 123-4567`). Inputs with fewer than 10 digits are returned unchanged so partial/invalid values still render.
 *
 * @param e164 - The raw phone number (E.164 or any string of digits with optional punctuation).
 * @returns The formatted phone string, or the original input when it has too few digits to format.
 */
export function FormatPhoneE164(e164: string): string {
  const digits = e164.replace(localConstants.phoneNonDigit, '');
  if (digits.length < localConstants.phoneLocalLength) return e164;

  const local = digits.slice(-localConstants.phoneLocalLength);
  const country = digits.slice(0, digits.length - localConstants.phoneLocalLength);
  const area = local.slice(0, localConstants.phoneAreaEnd);
  const exchange = local.slice(localConstants.phoneAreaEnd, localConstants.phoneExchangeEnd);
  const line = local.slice(localConstants.phoneExchangeEnd);

  const localFormatted = `(${area}) ${exchange}-${line}`;
  return country ? `+${country} ${localFormatted}` : localFormatted;
}

/** A formatted answer split into parent content and nested collapsible sections. */
export interface ISectionedTextDTO {
  head: string; // Parent content before the first section (empty when the text opens with a section)
  sections: { title: string; body: string }[]; // Nested sections, in order, each keyed on its sub-header marker run
  tail: string; // Parent content after the last section (empty when the text ends inside a section)
}

/**
 * Splits a Localized answer string into parent head/tail content and nested sections based on the existing inline-block formatting indicators: chunks are blank-line separated, and a chunk
 * containing a `{0}...{0}` sub-header run opens a nested section whose title is that run and whose body is the rest of the chunk. Chunks before the first section form the head, chunks after the
 * last section form the tail. Answers with no sub-header runs return the full text as head and no sections, so callers render unchanged.
 *
 * @param template - The Localized string, possibly containing sub-header-marked sections.
 * @returns The sectioned text.
 */
export function SplitNestedSections(template: string): ISectionedTextDTO {
  const marker = new RegExp(`\\{${localConstants.nestedSectionMarkerIndex}\\}([\\s\\S]*?)\\{${localConstants.nestedSectionMarkerIndex}\\}`);
  const head: string[] = [];
  const tail: string[] = [];
  const sections: { title: string; body: string }[] = [];
  for (const chunk of template.split(localConstants.blankLinePattern)) {
    const match = marker.exec(chunk);
    if (match) {
      sections.push({ title: match[1].trim(), body: chunk.replace(marker, '').trim() });
    } else if (sections.length === 0) {
      head.push(chunk);
    } else {
      tail.push(chunk);
    }
  }
  return { head: head.join(localConstants.paragraphJoin), sections, tail: tail.join(localConstants.paragraphJoin) };
}

/**
 * Splits a plain run of text on newlines, emitting a <br/> between segments. Empty segments (e.g. a trailing or doubled newline) contribute only the break, so blank lines are preserved.
 *
 * @param text - The plain text run (no inline-block markers).
 * @param keyPrefix - A stable prefix used to derive React keys for the emitted nodes.
 * @returns The text split into text/`<br/>` nodes.
 */
function RenderTextRun(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  text.split(localConstants.lineBreak).forEach((segment, index) => {
    if (index > 0) nodes.push(createElement(localConstants.brTag, { key: `${keyPrefix}-br-${index}` }));
    if (segment.length > 0) nodes.push(createElement(Fragment, { key: `${keyPrefix}-t-${index}` }, segment));
  });
  return nodes;
}

/**
 * Renders a Localized string that uses inline-block markup into React nodes. Each `{N}...{N}` run is wrapped in a `<span>` carrying `classNames[N]`, and every literal newline becomes a `<br/>`.
 *
 * The markup is decorative and resolved per call site: the caller supplies the class for each index, so the same string can render differently in different surfaces. A consumer that only needs
 * line breaks (no styled runs) passes an empty `classNames` array. Markers are ignored by the translator, exactly like positional interpolation params.
 *
 * @param template - The Localized string, possibly containing `{N}...{N}` runs and `\n` breaks.
 * @param classNames - Style classes indexed by marker number; `classNames[N]` styles every `{N}...{N}` run.
 * @returns The rendered nodes, ready to drop into a container element.
 */
export function FormatInlineBlocks(template: string, classNames: readonly string[]): ReactNode[] {
  const pattern = new RegExp(localConstants.inlineBlockPattern.source, localConstants.inlineBlockPattern.flags);
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (let match = pattern.exec(template); match !== null; match = pattern.exec(template)) {
    if (match.index > lastIndex) nodes.push(...RenderTextRun(template.slice(lastIndex, match.index), `pre-${key}`));
    const className = classNames[Number(match[1])];
    nodes.push(createElement(localConstants.spanTag, { key: `block-${key}`, className }, RenderTextRun(match[2], `block-${key}`)));
    lastIndex = pattern.lastIndex;
    key += 1;
  }

  if (lastIndex < template.length) nodes.push(...RenderTextRun(template.slice(lastIndex), `tail-${key}`));
  return nodes;
}
